/**
 * Inventory Section Component
 * POV-0110 / POV-0111: Section 6 of the Part Overview Panel
 *
 * Per PRD Section 11.2 + Completion Prompt Phase B:
 * - Stock summary by site (On Hand / Available / Allocated / QC Hold / Quarantine)
 * - Lot details table with status badges and expiry indicators
 * - Lot detail panel (on click) with full info
 * - Controlled actions: Quarantine 🔒, Release 🔒 (with reason-for-change)
 * - AS9100D Guidance banner (Clause 8.5.4)
 * - Quick links to existing inventory module pages
 *
 * section_key: "inventory"
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Package,
  Loader2,
  AlertTriangle,
  Info,
  Clock,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Lock,
  ExternalLink,
  RefreshCw,
  ChevronRight,
  Filter,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ViewRelatedHistoryLink } from './HistorySection';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import api from '../../services/api';

const SECTION_KEY = 'inventory';

// --- Status config ---
const LOT_STATUS_CONFIG = {
  RELEASED: { label: 'Available', className: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck },
  QC_HOLD: { label: 'QC Hold', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  QUARANTINE: { label: 'Quarantine', className: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert },
  EXPIRED: { label: 'Expired', className: 'bg-slate-200 text-slate-600 border-slate-300', icon: ShieldOff },
  CONSUMED: { label: 'Consumed', className: 'bg-slate-100 text-slate-500 border-slate-200', icon: null },
};

const EXPIRY_STATUS_CONFIG = {
  expired: { label: 'Expired', className: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  expiring_30d: { label: '< 30d', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock },
  expiring_90d: { label: '< 90d', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: null },
  no_expiry: { label: '—', className: '', icon: null },
};

const LOT_FILTER_OPTIONS = [
  { value: 'all', label: 'All Lots' },
  { value: 'RELEASED', label: 'Available' },
  { value: 'QC_HOLD', label: 'QC Hold' },
  { value: 'QUARANTINE', label: 'Quarantine' },
  { value: 'EXPIRED', label: 'Expired' },
];

const REASON_CODES = [
  { code: 'quality_hold', label: 'Quality Hold' },
  { code: 'quality_approval', label: 'Quality Approval' },
  { code: 'inspection_fail', label: 'Inspection Failure' },
  { code: 'inspection_pass', label: 'Inspection Pass' },
  { code: 'customer_complaint', label: 'Customer Complaint' },
  { code: 'counterfeit_suspect', label: 'Counterfeit Suspect' },
  { code: 'regulatory_hold', label: 'Regulatory Hold' },
  { code: 'regulatory_clearance', label: 'Regulatory Clearance' },
  { code: 'shelf_life_expired', label: 'Shelf Life Expired' },
  { code: 'damage_discovered', label: 'Damage Discovered' },
  { code: 'data_correction', label: 'Data Correction' },
  { code: 'other', label: 'Other' },
];

// --- Sub-components ---

const StatusBadge = ({ status }) => {
  const config = LOT_STATUS_CONFIG[status];
  if (!config) return <span className="text-xs text-slate-400">{status || '—'}</span>;
  const IconComp = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border', config.className)}>
      {IconComp && <IconComp className="w-3 h-3" />}
      {config.label}
    </span>
  );
};

const ExpiryBadge = ({ status, days }) => {
  const config = EXPIRY_STATUS_CONFIG[status];
  if (!config || status === 'active' || status === 'no_expiry') return null;
  const IconComp = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border', config.className)}>
      {IconComp && <IconComp className="w-2.5 h-2.5" />}
      {status === 'expired' ? 'Expired' : `${Math.abs(days || 0)}d`}
    </span>
  );
};

/** AS9100D Guidance Banner */
const GuidanceBanner = () => (
  <div className="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs" data-testid="guidance-banner-inventory">
    <div className="flex items-start gap-2">
      <Info className="w-3.5 h-3.5 text-sky-600 mt-0.5 shrink-0" />
      <div>
        <p className="font-semibold text-sky-800">AS9100D Clause: 8.5.4 (Preservation)</p>
        <ul className="mt-1 space-y-0.5 text-sky-700">
          <li>Preserve product outputs during production and delivery</li>
          <li>Shelf life control and stock rotation (FIFO/FEFO)</li>
          <li>Lot/serial traceability for batch recalls and quality investigations</li>
          <li>Quarantine controls for nonconforming material (Clause 8.7)</li>
        </ul>
      </div>
    </div>
  </div>
);

/** Alert Badges Bar */
const AlertBar = ({ alerts }) => {
  if (!alerts) return null;
  const items = [];
  if (alerts.has_quarantine) items.push({ label: `${alerts.quarantine_count} Quarantined`, className: 'bg-red-50 text-red-700 border-red-200', icon: ShieldAlert });
  if (alerts.has_qc_hold) items.push({ label: `${alerts.qc_hold_count} QC Hold`, className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock });
  if (alerts.has_expired) items.push({ label: `${alerts.expired_count} Expired`, className: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle });
  if (alerts.has_expiring_soon) items.push({ label: `${alerts.expiring_soon_count} Expiring Soon`, className: 'bg-orange-50 text-orange-700 border-orange-200', icon: Clock });
  if (items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="inventory-alert-bar">
      {items.map((item, i) => {
        const IconComp = item.icon;
        return (
          <div key={i} className={cn('flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border', item.className)}>
            <IconComp className="w-3.5 h-3.5" />
            <span className="font-medium">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};

/** Stock Summary Cards */
const StockSummaryCards = ({ summary }) => {
  if (!summary) return null;
  const metrics = [
    { label: 'On Hand', value: summary.total_on_hand, color: 'text-slate-900' },
    { label: 'Available', value: summary.total_available, color: 'text-emerald-700' },
    { label: 'Allocated', value: summary.total_allocated, color: 'text-blue-700' },
    { label: 'QC Hold', value: summary.total_qc_hold, color: summary.total_qc_hold > 0 ? 'text-amber-700' : 'text-slate-400' },
    { label: 'Quarantine', value: summary.total_quarantine, color: summary.total_quarantine > 0 ? 'text-red-700' : 'text-slate-400' },
  ];

  return (
    <div className="mb-4" data-testid="stock-summary-cards">
      <div className="grid grid-cols-5 gap-2">
        {metrics.map((m) => (
          <div key={m.label} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{m.label}</p>
            <p className={cn('text-lg font-bold font-mono', m.color)}>{m.value}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-1 text-right">
        {summary.lot_count} lot{summary.lot_count !== 1 ? 's' : ''} across{' '}
        {summary.location_count} location{summary.location_count !== 1 ? 's' : ''} · UOM: {summary.uom}
      </p>
    </div>
  );
};

/** Stock by Site Table */
const SiteBreakdown = ({ sites }) => {
  if (!sites || sites.length === 0) return null;

  return (
    <div className="mb-4" data-testid="site-breakdown">
      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">By Warehouse</h4>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-100">
            <TableHead className="text-[10px] font-semibold">Site</TableHead>
            <TableHead className="text-[10px] font-semibold text-right">On Hand</TableHead>
            <TableHead className="text-[10px] font-semibold text-right">Available</TableHead>
            <TableHead className="text-[10px] font-semibold text-right">Allocated</TableHead>
            <TableHead className="text-[10px] font-semibold text-right">Locations</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sites.map((site, i) => (
            <TableRow key={i} className="hover:bg-slate-50">
              <TableCell className="text-xs font-medium">{site.site}</TableCell>
              <TableCell className="text-xs text-right font-mono">{site.qty_on_hand}</TableCell>
              <TableCell className="text-xs text-right font-mono text-emerald-700">{site.qty_available}</TableCell>
              <TableCell className="text-xs text-right font-mono text-blue-700">{site.qty_allocated}</TableCell>
              <TableCell className="text-xs text-right text-slate-500">{site.location_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/** Lot Detail Panel */
const LotDetailPanel = ({ lot, onClose, onQuarantine, onRelease }) => {
  if (!lot) return null;
  const canQuarantine = lot.status === 'RELEASED' || lot.status === 'QC_HOLD';
  const canRelease = lot.status === 'QUARANTINE' || lot.status === 'QC_HOLD';

  return (
    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="lot-detail-panel">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-slate-700">Lot: {lot.lot_number || lot.lot_id}</h4>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <div><span className="text-slate-500">Status:</span> <StatusBadge status={lot.status} /></div>
        <div><span className="text-slate-500">Quantity:</span> <span className="font-mono font-bold">{lot.quantity}</span></div>
        {lot.manufacture_date && <div><span className="text-slate-500">Mfg Date:</span> {lot.manufacture_date.split('T')[0]}</div>}
        {lot.expiry_date && <div><span className="text-slate-500">Expiry:</span> {lot.expiry_date.split('T')[0]} <ExpiryBadge status={lot.expiry_status} days={lot.days_to_expiry} /></div>}
        {lot.supplier_name && <div><span className="text-slate-500">Supplier:</span> {lot.supplier_name}</div>}
        {lot.supplier_lot_number && <div><span className="text-slate-500">Supplier Lot:</span> {lot.supplier_lot_number}</div>}
        {lot.heat_number && <div><span className="text-slate-500">Heat/Batch:</span> {lot.heat_number}</div>}
        {lot.received_ref && <div><span className="text-slate-500">Received:</span> {lot.received_ref}</div>}
        {lot.location_display && <div className="col-span-2"><span className="text-slate-500">Location:</span> {lot.location_display}{lot.bln_code ? ` (BLN: ${lot.bln_code})` : ''}</div>}
        {lot.part_revision && <div><span className="text-slate-500">Revision:</span> {lot.part_revision}</div>}
        <div className="col-span-2 flex items-center gap-2 mt-1">
          {lot.is_hazmat && <span className="text-[9px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded border border-yellow-200 font-medium">HAZMAT</span>}
          {lot.is_esd_sensitive && <span className="text-[9px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded border border-purple-200 font-medium">ESD</span>}
          {lot.has_coc && <span className={cn('text-[9px] px-1.5 py-0.5 rounded border font-medium', lot.coc_verified ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200')}>CoC {lot.coc_verified ? '✓' : '?'}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-200">
        {canQuarantine && (
          <Button size="sm" variant="destructive" className="text-[10px] h-7" onClick={() => onQuarantine(lot)}>
            <Lock className="w-3 h-3 mr-1" /> Quarantine 🔒
          </Button>
        )}
        {canRelease && (
          <Button size="sm" variant="default" className="text-[10px] h-7 bg-emerald-600 hover:bg-emerald-700" onClick={() => onRelease(lot)}>
            <ShieldCheck className="w-3 h-3 mr-1" /> Release 🔒
          </Button>
        )}
      </div>
    </div>
  );
};

/** Quick Links to Full Inventory Pages */
const QuickLinks = ({ partId }) => {
  const links = [
    { label: 'Stock Inquiry', href: '/inventory/stock', icon: Package },
    { label: 'Lot Management', href: '/inventory/lots', icon: Package },
    { label: 'GRN History', href: '/inventory/grn', icon: Package },
  ];

  return (
    <div className="flex flex-wrap gap-2 mt-3 mb-3" data-testid="inventory-quick-links">
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1"
        >
          <ExternalLink className="w-2.5 h-2.5" />
          {link.label}
        </a>
      ))}
    </div>
  );
};

/** Controlled Action Dialog (Quarantine / Release) */
const ControlledActionDialog = ({ open, onClose, onConfirm, actionType, lotNumber, isSubmitting }) => {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');

  const handleConfirm = () => {
    if (!reasonCode) { toast.error('Reason code is required'); return; }
    if (!reasonText.trim()) { toast.error('Reason text is required'); return; }
    onConfirm(reasonCode, reasonText.trim());
  };

  const handleOpenChange = (v) => {
    if (!v) {
      setReasonCode('');
      setReasonText('');
      onClose();
    }
  };

  const isQuarantine = actionType === 'quarantine';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <Lock className="w-4 h-4 text-amber-600" />
            {isQuarantine ? 'Quarantine Lot' : 'Release Lot'} — Controlled Change
          </DialogTitle>
          <DialogDescription className="text-xs">
            You are modifying a controlled field: <strong>Lot Status</strong> for <strong>{lotNumber}</strong>.
            A reason for change is required per AS9100D {isQuarantine ? '8.7' : '8.6'}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Reason Code *</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select reason..." /></SelectTrigger>
              <SelectContent>
                {REASON_CODES.map((rc) => (
                  <SelectItem key={rc.code} value={rc.code} className="text-xs">{rc.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reason Text *</Label>
            <Textarea
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Describe the reason for this change..."
              className="text-xs mt-1 h-20 resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} className="text-xs h-8" disabled={isSubmitting}>Cancel</Button>
          <Button
            size="sm"
            className={cn('text-xs h-8', isQuarantine ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700')}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            {isQuarantine ? 'Confirm Quarantine' : 'Confirm Release'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --- Main Component ---

const InventorySection = ({ partId, onViewRelatedHistory }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [lotFilter, setLotFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, lot: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    if (!partId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v1/inventory-overview/${partId}`);
      setData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setData(null);
        setError('Part not found');
      } else {
        setError(err.response?.data?.detail || 'Failed to load inventory data');
      }
    } finally {
      setLoading(false);
    }
  }, [partId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filter lots
  const filteredLots = data?.lots?.filter((lot) => {
    if (lotFilter === 'all') return true;
    return lot.status === lotFilter;
  }) || [];

  // Handle controlled action (quarantine/release)
  const handleAction = async (reasonCode, reasonText) => {
    const { type, lot } = actionDialog;
    if (!lot) return;
    setIsSubmitting(true);
    try {
      const endpoint = type === 'quarantine'
        ? `/api/v1/inventory-overview/${partId}/quarantine`
        : `/api/v1/inventory-overview/${partId}/release`;
      await api.post(endpoint, {
        lot_id: lot.lot_id,
        reason_code: reasonCode,
        reason_text: reasonText,
      });
      toast.success(`Lot ${lot.lot_number || lot.lot_id} ${type === 'quarantine' ? 'quarantined' : 'released'}`);
      setActionDialog({ open: false, type: null, lot: null });
      setSelectedLot(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${type} lot`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse" data-testid="inventory-loading">
        <div className="h-16 bg-slate-100 rounded-lg" />
        <div className="grid grid-cols-5 gap-2">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded" />)}</div>
        <div className="h-32 bg-slate-100 rounded-lg" />
      </div>
    );
  }

  // Error state
  if (error && !data) {
    return (
      <div className="text-center py-6">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-600">{error}</p>
        <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={loadData}>Retry</Button>
      </div>
    );
  }

  const isEmpty = !data || data.stock_summary?.total_on_hand === 0;

  return (
    <div className="space-y-2" data-testid="inventory-section">
      {/* AS9100D Guidance Banner */}
      <GuidanceBanner />

      {/* Refresh */}
      <div className="flex items-center justify-end">
        <button onClick={loadData} className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700" data-testid="refresh-inventory">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Alerts */}
      <AlertBar alerts={data?.alerts} />

      {/* Stock Summary */}
      <StockSummaryCards summary={data?.stock_summary} />

      {/* Traceability Type */}
      {data?.traceability_type && data.traceability_type !== 'none' && (
        <div className="text-xs text-slate-600 mb-2" data-testid="traceability-type">
          Traceability: <span className="font-semibold capitalize">{data.traceability_type} Controlled</span>
        </div>
      )}

      {/* Site Breakdown (only if multiple sites) */}
      {data?.stock_summary?.by_site?.length > 1 && (
        <SiteBreakdown sites={data.stock_summary.by_site} />
      )}

      {/* Lot Details Table */}
      {!isEmpty && (
        <div data-testid="lot-details-section">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Lot Details</h4>
            <div className="flex items-center gap-2">
              <Filter className="w-3 h-3 text-slate-400" />
              <Select value={lotFilter} onValueChange={setLotFilter}>
                <SelectTrigger className="h-6 text-[10px] w-[120px] border-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOT_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {filteredLots.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-3 text-center">No lots match filter</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="text-[10px] font-semibold">Lot #</TableHead>
                  <TableHead className="text-[10px] font-semibold text-right">Qty</TableHead>
                  <TableHead className="text-[10px] font-semibold">Status</TableHead>
                  <TableHead className="text-[10px] font-semibold">Mfg Date</TableHead>
                  <TableHead className="text-[10px] font-semibold">Expiry</TableHead>
                  <TableHead className="text-[10px] font-semibold">Location</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLots.map((lot) => (
                  <TableRow
                    key={lot.lot_id}
                    className={cn('hover:bg-blue-50 cursor-pointer', selectedLot?.lot_id === lot.lot_id && 'bg-blue-50 ring-1 ring-blue-200')}
                    onClick={() => setSelectedLot(selectedLot?.lot_id === lot.lot_id ? null : lot)}
                    data-testid={`lot-row-${lot.lot_id}`}
                  >
                    <TableCell className="text-xs font-mono font-medium">{lot.lot_number || lot.lot_id.slice(0, 12)}</TableCell>
                    <TableCell className="text-xs text-right font-mono font-bold">{lot.quantity}</TableCell>
                    <TableCell><StatusBadge status={lot.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{lot.manufacture_date?.split('T')[0] || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {lot.expiry_date ? lot.expiry_date.split('T')[0] : '—'}
                      {lot.expiry_status && lot.expiry_status !== 'no_expiry' && lot.expiry_status !== 'active' && (
                        <span className="ml-1"><ExpiryBadge status={lot.expiry_status} days={lot.days_to_expiry} /></span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {lot.bln_code ? <span className="font-mono bg-amber-100 text-amber-800 px-1 rounded text-[10px]">{lot.bln_code}</span> : null}
                      {lot.location_display ? <span className="ml-1">{lot.location_display}</span> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Selected Lot Detail */}
          <LotDetailPanel
            lot={selectedLot}
            onClose={() => setSelectedLot(null)}
            onQuarantine={(lot) => setActionDialog({ open: true, type: 'quarantine', lot })}
            onRelease={(lot) => setActionDialog({ open: true, type: 'release', lot })}
          />
        </div>
      )}

      {/* Empty State */}
      {isEmpty && (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200" data-testid="inventory-empty">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 font-medium">No inventory recorded</p>
          <p className="text-xs text-slate-400 mt-1">
            Use the Inventory module to receive and track stock for this part
          </p>
        </div>
      )}

      {/* Quick Links */}
      <QuickLinks partId={partId} />

      {/* View Related History */}
      <ViewRelatedHistoryLink sectionKey={SECTION_KEY} onClick={onViewRelatedHistory} />

      {/* Controlled Action Dialog */}
      <ControlledActionDialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, type: null, lot: null })}
        onConfirm={handleAction}
        actionType={actionDialog.type}
        lotNumber={actionDialog.lot?.lot_number || actionDialog.lot?.lot_id}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default InventorySection;
